import type React from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

const StyledButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger' | 'default'
  $size?: 'sm' | 'md' | 'lg'
}>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return `${theme.spacing.xs} ${theme.spacing.sm}`
      case 'lg':
        return `${theme.spacing.md} ${theme.spacing.lg}`
      default:
        return `${theme.spacing.sm} ${theme.spacing.md}`
    }
  }};
  border-radius: ${theme.borderRadius.md};
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return theme.typography.sm
      case 'lg':
        return theme.typography.sm
      default:
        return theme.typography.md
    }
  }};
  font-weight: ${({ $variant }) => ($variant === 'primary' ? 600 : $variant === 'danger' ? 500 : 300)};
  cursor: pointer;
  transition: ${theme.transitions.default};
  border: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${({ theme, $variant }) => {
    switch ($variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.textInverted};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryDark};
            border-color: ${theme.colors.primaryDark};
            color: ${theme.colors.textInverted};
          }
        `
      case 'danger':
        return `
          background-color: none;
          color: ${theme.colors.error};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.error};
            border-color: ${theme.colors.error};
            color: #ffffff;
          }
        `
      case 'secondary':
        return `
          background-color: ${theme.colors.surface};
          color: ${theme.colors.text};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.surfaceHover};
            color: ${theme.colors.text};
          }
        `
      default:
        return `
          background: none;
          color: ${theme.colors.textMuted};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.surfaceHover};
            color: ${theme.colors.text};
          }
        `
    }
  }}
`

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'default'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
  title?: string
  className?: string
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'default',
  size = 'md',
  type = 'button',
  disabled = false,
  style,
  onClick,
  children,
  title,
  className,
}) => {
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      type={type}
      style={style}
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={className}
    >
      {children}
    </StyledButton>
  )
}
